import { QUESTION_TYPES } from '@api/instant_survey';
import type { Group, InstantSurvey, Question } from '@api/instant_survey/types';

import { SURVEY_SOURCE_LANGUAGE } from '@constants';
import { getOrderedSurveyLanguages } from '@helpers';

import { replaceScreenerPromptTokensWithValueMap } from '../../../pages/CreateInstantSurvey/components/Questionnaire/components/ModalScreenerQuestion/utilities';

interface TranslationEntry {
  _delete?: boolean;
  languageCode?: string;
  text?: string;
}

/**
 * Returns an item's stored translated label for the given language, or undefined to fall back to
 * the source string — when no language is selected, the language is the source language (no entry
 * exists), or the item has no translation for it.
 */
const getTranslatedLabel = (item: Item, language?: string) => {
  if (!language) return undefined;
  const translations = (item.translations ?? []) as TranslationEntry[];
  const entry = translations.find(
    ({ _delete, languageCode }) => languageCode === language && !_delete
  );
  return entry?.text || undefined;
};

export const getDropdownOptions = (items: Item[], language?: string) => {
  let questionNumber = 0;

  return items.map((item) => {
    const isQuotaGroup = 'name' in item;
    const sourceLabel = isQuotaGroup ? item.name : item.text;
    const label = getTranslatedLabel(item, language) ?? sourceLabel;
    const questionLabel =
      !isQuotaGroup && item.isScreener
        ? replaceScreenerPromptTokensWithValueMap(label, item.screenerValueMap)
        : label;
    const isInstructionalText = !isQuotaGroup && item.type === QUESTION_TYPES.INSTRUCTIONS.value;
    const marker = isInstructionalText ? 'T' : `${questionNumber + 1}.`;

    if (!isQuotaGroup && !isInstructionalText) questionNumber += 1;

    return {
      label: isQuotaGroup ? label : `${marker} ${questionLabel}`,
      value: item.id,
    };
  });
};

export const getIsPreviewDisabled = (questions?: Question[]) => !questions?.length;

/**
 * Derives the ordered language list for the preview language switcher from a survey's
 * languageInfo: the source (original) language first, then each translated language (de-duped).
 * Falls back to English as the source language when languageInfo has no originalLanguage.
 */
export const getSurveyLanguages = (languageInfo?: InstantSurvey['languageInfo']) => {
  const languages = getOrderedSurveyLanguages(languageInfo);
  const [sourceLanguage] = languages;
  return { languages, sourceLanguage: sourceLanguage || SURVEY_SOURCE_LANGUAGE };
};

export type Item = Group | Question;

export interface PreviewSelection {
  language?: string;
  questionId?: number;
  quotaGroupId?: number;
  /**
   * Monotonic counter used to force a new preview session when restarting from the
   * same question and quota group selection.
   */
  restartKey?: number;
}
